import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ClipLoader, PulseLoader, BeatLoader } from 'react-spinners';

// Define styled components with TypeScript
interface SpinnerContainerProps {
  overlay?: boolean;
  zIndex?: number;
}

const SpinnerContainer = styled(motion.div)<SpinnerContainerProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.overlay ? 'rgba(255, 255, 255, 0.8)' : 'transparent'};
  z-index: ${props => props.zIndex || 1000};
  
  &.overlay {
    background-color: rgba(255, 255, 255, 0.8);
  }
`;

const SpinnerText = styled(motion.p)`
  margin-top: 20px;
  font-size: 1rem;
  color: var(--primary-color);
  font-weight: 500;
`;

// Animation variants
const spinnerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.3 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.3 }
  }
};

const textVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      delay: 0.2,
      duration: 0.3
    }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

// Define component props interface
interface LoadingSpinnerProps {
  type?: 'clip' | 'pulse' | 'beat';
  color?: string;
  size?: number;
  overlay?: boolean;
  text?: string;
  zIndex?: number;
}

/**
 * LoadingSpinner Component
 * @param {string} type - Type of spinner: 'clip', 'pulse', or 'beat'
 * @param {string} color - Color of the spinner (default: primary color)
 * @param {number} size - Size of the spinner in pixels
 * @param {boolean} overlay - Whether to show a background overlay
 * @param {string} text - Optional text to display below the spinner
 * @param {number} zIndex - Z-index of the spinner container
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  type = 'clip', 
  color = 'var(--primary-color)', 
  size = 50, 
  overlay = true,
  text,
  zIndex
}) => {
  let Spinner: React.ReactElement;
  
  switch (type) {
    case 'pulse':
      Spinner = <PulseLoader color={color} size={size / 3} />;
      break;
    case 'beat':
      Spinner = <BeatLoader color={color} size={size / 2} />;
      break;
    case 'clip':
    default:
      Spinner = <ClipLoader color={color} size={size} />;
  }
  
  return (
    <SpinnerContainer 
      overlay={overlay}
      zIndex={zIndex}
      variants={spinnerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={overlay ? 'overlay' : ''}
    >
      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 0, 0]
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatType: "loop"
        }}
      >
        {Spinner}
      </motion.div>
      
      {text && (
        <SpinnerText
          variants={textVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {text}
        </SpinnerText>
      )}
    </SpinnerContainer>
  );
};

export default LoadingSpinner;
